/**
 * Build the kind-discriminated leaf fields of a WorkflowDefinition from the
 * loaded workflow row.
 *
 * The backend WorkflowDefinition pydantic validator is strict per kind:
 *   - canonical     → direction + canonical REQUIRED, app_id forbidden
 *   - orchestration → direction + canonical MUST be null
 *   - app           → app_id REQUIRED, direction + canonical MUST be null
 *
 * Every save path in the editor MUST emit the shape matching the workflow's
 * own kind. Emitting "orchestration" for an app workflow (the pre-fix
 * default-else behaviour) trips the version-create guard
 * "definition.kind (orchestration) differs from workflow.kind (app)" and
 * blocks the save entirely (ADR-0010 D10). Centralising the mapping here
 * keeps all three build sites (buildLiveDefinition, the main save, and the
 * sub-flow parent rebuild) in agreement.
 */
import type {
  Direction,
  WorkflowDefinition,
  WorkflowKind,
} from "@/lib/api-workflows";

export interface WorkflowKindSource {
  kind?: WorkflowKind | null;
  direction?: Direction | null;
  canonical_model?: string | null;
  canonical_version?: number | null;
  app_id?: string | null;
}

export function definitionKindFields(
  wf: WorkflowKindSource,
): Pick<WorkflowDefinition, "kind" | "direction" | "canonical" | "app_id"> {
  const kind = (wf.kind ?? "canonical") as WorkflowKind;
  if (kind === "canonical") {
    return {
      kind: "canonical",
      direction: wf.direction ?? undefined,
      canonical:
        wf.canonical_model && wf.canonical_version
          ? { model: wf.canonical_model, version: wf.canonical_version }
          : undefined,
    };
  }
  if (kind === "app") {
    return {
      kind: "app",
      direction: null,
      canonical: null,
      app_id: wf.app_id ?? null,
    };
  }
  return { kind: "orchestration", direction: null, canonical: null };
}
